import { useCallback, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Input,
  Label,
} from "@repo/ui";
import { ArrowLeft, CheckCircle, Loader2, Mail } from "lucide-react";

import { forgotPharmacyPassword, verifyPharmacyReset } from "@/lib/api/auth";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

type PageState = "idle" | "code_sent" | "success";

function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [pageState, setPageState] = useState<PageState>("idle");
  const [obfuscatedEmail, setObfuscatedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendCode = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setError("");

      const normalized = email.trim().toLowerCase();
      if (!normalized) return;

      setIsSending(true);

      try {
        const data = await forgotPharmacyPassword({ email: normalized });
        setObfuscatedEmail(
          (data as { obfuscatedEmail?: string }).obfuscatedEmail ?? ""
        );
        setPageState("code_sent");
      } catch (err) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || "Something went wrong. Please try again.");
      } finally {
        setIsSending(false);
      }
    },
    [email]
  );

  const handleVerify = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");

      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }

      setIsSubmitting(true);

      try {
        await verifyPharmacyReset({ otp, newPassword, confirmPassword });
        setPageState("success");
      } catch (err) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || "Something went wrong. Please try again.");
        setOtp("");
      } finally {
        setIsSubmitting(false);
      }
    },
    [otp, newPassword, confirmPassword]
  );

  if (pageState === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-4 pt-10 pb-8 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <div>
              <h2 className="text-xl font-semibold">Password Reset</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Your password has been updated. You can now sign in with your
                new password.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate({ to: "/login" })}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-1 flex items-center gap-3">
            <Link
              to="/login"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
          </div>
          <CardDescription>
            {pageState === "idle" &&
              "Enter your pharmacy account email and we'll send a verification code."}
            {pageState === "code_sent" &&
              `Enter the 6-digit code sent to ${obfuscatedEmail} and choose a new password.`}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {pageState === "idle" && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@pharmacy.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSending}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isSending || !email.trim()}
              >
                {isSending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Verification Code
                  </>
                )}
              </Button>
            </form>
          )}

          {pageState === "code_sent" && (
            <form
              onSubmit={handleVerify}
              autoComplete="off"
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  required
                  disabled={isSubmitting}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
                <p className="text-muted-foreground text-xs">
                  At least 8 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isSubmitting ||
                  otp.length !== 6 ||
                  newPassword.length < 8 ||
                  confirmPassword.length < 8
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setPageState("idle");
                  setOtp("");
                  setError("");
                }}
                className="text-muted-foreground hover:text-foreground w-full text-center text-sm transition-colors"
              >
                Didn&apos;t receive the code? Try again
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
